import React from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LoadingOutlined } from "@ant-design/icons";
import { WHITE, PRIMARY_300, PRIMARY_600 } from "../../constant/colors";
import { selectBulkActionLoading, selectBulkActionNavigatedAway } from "../../store/selector";

const ToastContainer = styled.div`
  /* Fixed positioning - centered horizontally like Ant Design toasts */
  position: fixed;
  top: 73px;
  left: 50%;
  z-index: 1060; /* Above Ant Design message toasts (typically ~1010) */

  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${WHITE};
  border: 1px solid ${PRIMARY_300};
  border-radius: 8px;
  padding: 6px 12px;
  box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.05), 0px 12px 16px -4px rgba(16, 24, 40, 0.1);
  line-height: 1;

  /* Animation - includes horizontal centering transform */
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transform: translateX(-50%) translateY(${(props) => (props.$visible ? "0" : "-10px")});
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.25s ease-out, transform 0.25s ease-out, visibility 0.25s ease-out;
`;

const MessageText = styled.span`
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #000000;
  white-space: nowrap;
  line-height: 1;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinnerIcon = styled(LoadingOutlined)`
  font-size: 14px;
  color: ${PRIMARY_600};
  animation: ${spin} 1s linear infinite;
`;

/**
 * Global toast notification that appears when a bulk action is in progress
 * AND the user has navigated away from the bulk actions page.
 * This ensures the user is aware that a bulk action is still running
 * even when they're on a different page.
 * It shows a loading spinner and "Bulk action in progress." message.
 */
const BulkActionInProgressToast = () => {
  const { t } = useTranslation();
  const isLoading = useSelector(selectBulkActionLoading);
  const hasNavigatedAway = useSelector(selectBulkActionNavigatedAway);

  const isVisible = isLoading && hasNavigatedAway;

  return (
    <ToastContainer $visible={isVisible} data-testid="bulk-action-in-progress-toast">
      <SpinnerIcon />
      <MessageText>{t("bulk_actions.loading.global_toast_message")}</MessageText>
    </ToastContainer>
  );
};

export default BulkActionInProgressToast;
